import React from 'react';

import { useUserDirectory } from './useUserDirectory';
import { getMessageStyle, getStatusBadgeStyle, getStatusLabel, styles } from './userDirectoryStyles';
import { LifecycleAction } from './userDirectoryTypes';

// A status-triggered action button. `when` is checked against the lowercased status;
// the first matching entry in the `actions` array wins.
export interface StatusAction {
  when: (status: string) => boolean;
  actionType: LifecycleAction;
  label: string;
  successMessage: (fullName: string) => string;
  // Original PasswordReset behavior: the generic "send reset email" button disappears once
  // an actionSuccess message is already showing, but the password-expired-specific one does not.
  hideWhileMessageShowing?: boolean;
}

// An informational message shown instead of a button (e.g. "unsuspend before resetting password").
export interface StatusNotice {
  when: (status: string) => boolean;
  message: string;
}

interface UserDirectoryPageProps {
  title: string;
  actions: StatusAction[];
  notices?: StatusNotice[];
}

export const UserDirectoryPage: React.FC<UserDirectoryPageProps> = ({ title, actions, notices = [] }) => {
  const {
    emailQuery,
    searchResult,
    error,
    isLoading,
    actionSuccess,
    handleInputChange,
    handleSearchSubmit,
    runLifecycleAction,
  } = useUserDirectory();

  const status = searchResult?.status.toLowerCase() ?? '';
  const activeAction = actions.find((a) => a.when(status));
  const activeNotice = notices.find((n) => n.when(status));

  const showAction = !!activeAction && !(activeAction.hideWhileMessageShowing && actionSuccess);
  const showNotice = !activeAction && !!activeNotice && !actionSuccess;

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>{title}</h2>

      <form onSubmit={handleSearchSubmit} style={styles.form}>
        <div style={styles.inputGroup}>
          <label htmlFor="emailQuery" style={styles.label}>Search User by Email</label>
          <div style={styles.searchRow}>
            <input
              type="email"
              id="emailQuery"
              disabled={isLoading}
              value={emailQuery}
              onChange={handleInputChange}
              placeholder="user@company.com"
              style={{
                ...styles.input,
                borderColor: error ? '#ff4d4f' : '#ccc',
                backgroundColor: isLoading ? '#f5f5f5' : '#fff',
              }}
            />
            <button type="submit" disabled={isLoading || !emailQuery} style={styles.searchButton}>
              {isLoading && !searchResult ? 'Searching...' : 'Search'}
            </button>
          </div>
        </div>
      </form>

      {error && <p style={styles.errorText}>{error}</p>}
      {actionSuccess && <p style={getMessageStyle(actionSuccess)}>{actionSuccess}</p>}

      {searchResult && (
        <div style={styles.resultCard}>
          <h3 style={styles.cardHeader}>Search Result</h3>

          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>Display Name:</span>
            <span style={styles.infoValue}>
              {searchResult.profile.displayName || `${searchResult.profile.firstName} ${searchResult.profile.lastName}`}
            </span>
          </div>

          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>Email:</span>
            <span style={styles.infoValue}>{searchResult.profile.email}</span>
          </div>

          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>Status:</span>
            <span style={getStatusBadgeStyle(searchResult.status)}>{getStatusLabel(searchResult.status)}</span>
          </div>

          {showAction && activeAction && (
            <button
              type="button"
              onClick={() => runLifecycleAction(activeAction.actionType, activeAction.successMessage)}
              disabled={isLoading}
              style={styles.actionButton}
            >
              {isLoading ? 'Processing...' : activeAction.label}
            </button>
          )}

          {showNotice && activeNotice && <p style={styles.errorText}>{activeNotice.message}</p>}
        </div>
      )}
    </div>
  );
};

export default UserDirectoryPage;
